import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchMediaAssets, generateDataFromUrl, type MediaAPIParams } from "../api/fetchMediaAssets";
import type { MediaItem } from "../types/mediaLibraryTypes";
import type { MediaFilterData, MediaFormFields } from "../types/mediaLibraryFormTypes";
import { useForm } from "react-hook-form";
import { addToFavourites } from "~/features/mediaLibrary/api/addToFavourites";
import { userIsPublic } from "~/domains/user/utils/roles";
import { trackAssetInteraction, trackAssetModalClose, trackSearchSubmitted } from "~/app/analytics/ga/mediaLibrary";
import { downloadAssetFile } from "~/common/utils/ApiHelper";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { CURRENT_LANGUAGE, DEFAULT_LANGUAGE } from "../../../utils/constants";

export const useMediaLibrary = (filterData: MediaFilterData) => {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [selectedMediaItem, setSelectedMediaItem] = useState<MediaItem>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const { val: loginPopupOpen, setTrue: openLoginPopup, setFalse: closeLoginPopup } = useBooleanState(false);
  const [firstTime, setFirstTime] = useState(true);

  const defaultValues = generateDataFromUrl();
  const formProps = useForm<MediaFormFields>({
    defaultValues,
  });
  const fetchFavourite = formProps.watch("fetchFavourite");

  const stableFilterOptions = useMemo(() => filterData, [filterData]);

  useEffect(() => {
    if (firstTime) {
      handleLanguagePreselection();
    }
    fetchMedia({ ...formProps.getValues(), page: 0 });
  }, []);

  const handleLanguagePreselection = () => {
    const preselectedLanguages: string[] = [];
    const params = new URLSearchParams(window.location.search);
    const selectedLanguageParam = params.get("selectedLanguages");

    if (firstTime && selectedLanguageParam === null) {
      const langCode = CURRENT_LANGUAGE === "zh_TW" ? "zh" : CURRENT_LANGUAGE;
      const shopLanguage = filterData.languages.find((lang) => lang.key === langCode);
      preselectedLanguages.push(shopLanguage ? langCode : DEFAULT_LANGUAGE);
    }

    const hasSelectedLanguage = selectedLanguageParam && selectedLanguageParam !== "";
    const parsedLanguages = hasSelectedLanguage ? selectedLanguageParam.split(",") : [];
    parsedLanguages.map((lang) => {
      if (!preselectedLanguages.includes(lang)) {
        const isLanguageInFilterOptions = filterData.languages.find((languageOption) => languageOption.key === lang);
        if (isLanguageInFilterOptions) {
          preselectedLanguages.push(lang);
        }
      }
    });
    formProps.setValue("languages", preselectedLanguages);
    setFirstTime(false);
  };

  const fetchMedia = useCallback(async (params: MediaAPIParams, reset = false) => {
    try {
      setError(null);
      setLoading(true);
      if (reset) {
        setMediaItems([]);
        setPage(0);
        setHasMore(true);
        const response = await fetchMediaAssets(params);
        setMediaItems(response || []);
        trackSearchSubmitted(formProps.getValues().q, response.length);
      } else {
        const response = await fetchMediaAssets(params);
        setMediaItems((prev) => [...prev, ...(response || [])]);
        setHasMore(!!(response && response.length > 0));
      }
    } catch (err) {
      setError("Failed to load media.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    handleFavouriteAfterLogin();
  }, [mediaItems]);

  const handleFavouriteAfterLogin = async () => {
    const assetId = window.sessionStorage.getItem("favAssetId");
    const isPreviewOpen = window.sessionStorage.getItem("isPreviewOpen") === "true";

    if (assetId && mediaItems.length !== 0) {
      const item = mediaItems.find((item) => item.id === assetId);
      if (item) {
        await handleFavouriteClick(assetId, !item.isFavourite, item.title, item.language, item.mediaType);

        const url = new URL(window.location.href);
        window.sessionStorage.setItem("favAssetId", "");
        window.sessionStorage.setItem("isPreviewOpen", "false");
        window.history.replaceState(null, "", url.toString());

        if (isPreviewOpen) {
          setSelectedMediaItem(item);
          setIsOpen(true);
        }
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const buffer = 500;
      const scrollTop = window.scrollY;
      const viewportHeight = window.innerHeight;
      const fullHeight = document.documentElement.scrollHeight;

      if (scrollTop + viewportHeight + buffer >= fullHeight && !loading && hasMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchMedia({ ...formProps.getValues(), page: nextPage });
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [page, loading, hasMore, formProps]);

  const handleCardClick = (item: MediaItem) => {
    setSelectedMediaItem(item);
    setIsOpen(true);
    trackAssetInteraction("Asset Previewed", item.id, item.title, item.language, item.mediaType);
  };

  const handleFavouriteClick = async (
    id: string,
    updatedFav: boolean,
    title?: string,
    language?: string,
    mediaType?: string,
  ) => {
    if (userIsPublic()) {
      openLoginPopup();
      window.sessionStorage.setItem("favAssetId", id);
      return;
    }
    const status = await addToFavourites({ assetID: id, addToFavourite: updatedFav });
    if (status === 200) {
      trackAssetInteraction(updatedFav ? "Bookmark Added" : "Bookmark Removed", id, title, language, mediaType);
      updateMediaItems(id, updatedFav);
    }
  };

  const clearFavFromSessionStorage = () => {
    window.sessionStorage.setItem("favAssetId", "");
    window.sessionStorage.setItem("isPreviewOpen", "false");
  };

  const updateMediaItems = (id: string, updatedFav: boolean) => {
    let updatedMediaList: MediaItem[];

    if (fetchFavourite) {
      updatedMediaList = mediaItems.filter((item) => item.id !== id);
    } else {
      updatedMediaList = mediaItems.map((item) => (item.id === id ? { ...item, isFavourite: updatedFav } : item));
    }
    setMediaItems(updatedMediaList);
  };

  const updateSelectedMediaItem = (mediaDetails: MediaItem) => {
    setSelectedMediaItem(mediaDetails);
  };

  const handleModalClose = () => {
    if (selectedMediaItem) {
      trackAssetModalClose(
        selectedMediaItem.id,
        selectedMediaItem.title,
        selectedMediaItem.language,
        selectedMediaItem.mediaType,
      );
    }
    setIsOpen(false);
  };

  const handleDownload = async (mediaItem: MediaItem) => {
    if (!mediaItem) {
      return;
    }

    try {
      setIsDownloading(true);
      trackAssetInteraction("Content Download", mediaItem.id, mediaItem.title, mediaItem.language, mediaItem.mediaType);
      const extension = getExtensionFromMediaType(mediaItem.mediaType);
      const fullFilename = [mediaItem.title, extension].filter(Boolean).join(".");
      await downloadAssetFile(mediaItem.downloadUrl, fullFilename);
    } catch (err) {
      setErrorSnackbarOpen(true);
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    formProps,
    mediaItems,
    selectedMediaItem,
    isOpen,
    loading,
    error,
    stableFilterOptions,
    errorSnackbarOpen,
    loginPopupOpen,
    isDownloading,
    handleCardClick,
    fetchMedia,
    setErrorSnackbarOpen,
    handleFavouriteClick,
    updateMediaItems,
    updateSelectedMediaItem,
    handleModalClose,
    handleDownload,
    openLoginPopup,
    closeLoginPopup,
    clearFavFromSessionStorage,
  };
};

function getExtensionFromMediaType(mediaType: string): string | undefined {
  const extension = mediaType.split("/")[1];
  if (extension === "jpeg") {
    return "jpg";
  } else if (extension === "octet-stream") {
    return undefined;
  }
  return extension;
}
